import { Vec2 } from "miglib";
import { Faction } from "./types";
import { World } from "./World";


export class InfluenceMap {

    private readonly when = new Map<string, number>();
    private readonly influences = new Map<string, Faction>();
    private readonly counts = new Map<Faction, number>();
    private readonly overworld: World;
    private readonly netherworld: World;

    constructor(overworld: World, netherworld: World) {
        this.overworld = overworld;
        this.netherworld = netherworld;
    }

    private key(x: number, y: number): string {
        return x + "," + y;
    }

    private incrementCount(faction: Faction): void {
        this.counts.set(faction, (this.counts.get(faction) ?? 0) + 1);
    }

    private decrementCount(faction: Faction): void {
        const current = this.counts.get(faction);
        if (current === undefined) return;
        if (current <= 1) {
            this.counts.delete(faction);
        } else {
            this.counts.set(faction, current - 1);
        }
    }

    set(x: number, y: number, influence: Faction): void {

        const key = this.key(x, y);

        const w = this.when.get(key);
        if (w && Date.now() < w + 5000)
            return;

        const previous = this.influences.get(key);

        if (previous === influence) return;

        if (previous !== undefined) {
            this.decrementCount(previous);
        }
        this.incrementCount(influence);
        this.influences.set(key, influence);
        this.when.set(key, Date.now());
    }

    get(x: number, y: number): Faction | undefined {
        return this.influences.get(this.key(x, y));
    }

    hasInfluence(x: number, y: number): boolean {
        return this.influences.has(this.key(x, y));
    }

    remove(x: number, y: number): boolean {
        const key = this.key(x, y);

        const w = this.when.get(key);
        if (w && Date.now() < w + 5000)
            return false;

        const previous = this.influences.get(key);
        if (previous === undefined) return false;

        this.decrementCount(previous);
        this.influences.delete(key);
        this.when.set(key, Date.now());
        return true;
    }

    getInfluenceCount(influence: Faction): number {
        return this.counts.get(influence) ?? 0;
    }

    get influenceCount(): number {
        return this.influences.size;
    }

    getAll(faction: Faction): Vec2[] {
        const result: { x: number; y: number; influence: Faction }[] = [];
        for (const [key, influence] of this.influences) {
            if (influence === faction) {
                const [xs, ys] = key.split(",");
                result.push({ x: Number(xs), y: Number(ys), influence });
            }
        }
        return result;
    }

    *[Symbol.iterator](): IterableIterator<{ x: number; y: number; influence: Faction }> {
        for (const [key, influence] of this.influences) {
            const [xs, ys] = key.split(",");
            yield { x: Number(xs), y: Number(ys), influence };
        }
    }
}